import { useMemo } from 'react';
import {
  CONVERSATION_TREES,
  availableNodes,
  nodeDepth,
  rootNodes,
  type ChartFact,
  type ConversationTree,
  type TreeNode,
  type TreeProgress,
} from '@astrally/shared';
import { useApp } from '../../state/AppState';
import { useI18n, type I18nKey, type I18nVars } from '../../i18n';

/**
 * Starter questions derived from the grounding fact pack that is already
 * computed for every chart. Deterministic and free: no model call, no network,
 * no randomness — the same chart always offers the same openers, so the list
 * does not shuffle under the user between renders.
 */

interface Suggestion {
  key: I18nKey;
  vars?: I18nVars;
  /**
   * The chip's leading glyph. Decorative ONLY — it is rendered aria-hidden and
   * never joined to the question, because the chip's text is sent verbatim as
   * the reader's own message and an emoji in the middle of it would be put in
   * their mouth.
   */
  emoji: string;
}

/** A quick reply as the chat renders it: a glyph and the question it sends. */
export interface SuggestionChip {
  emoji: string;
  /** Exactly what is sent when the chip is tapped. */
  text: string;
}

/** Four fits one chip row on the narrowest phone before wrapping twice. */
const MAX = 4;

/**
 * The quick replies, with their glyphs. Derived from the same grounding fact
 * pack as before — deterministic and free, no model call and no randomness.
 */
export function useAllySuggestionChips(): SuggestionChip[] {
  const { t } = useI18n();
  const { facts } = useApp();

  return useMemo(() => {
    const picks: Suggestion[] = [{ key: 'ally.suggest.personality', emoji: '🪞' }];

    // The caveat outranks chart detail: when the birth time is not exact, the
    // first thing worth offering is what Ally can still answer without it.
    if (facts.some((f) => f.id.startsWith('unavailable.'))) {
      picks.push({ key: 'ally.suggest.uncertain', emoji: '⏳' });
    }

    const lord = leadingName(byId(facts, 'jyotish.dasha.current')?.value);
    picks.push(
      lord
        ? { key: 'ally.suggest.dashaLord', vars: { lord }, emoji: '🌀' }
        : { key: 'ally.suggest.dasha', emoji: '🌀' },
    );

    const saturn = leadingName(byId(facts, 'gochar.saturn')?.value);
    if (saturn) picks.push({ key: 'ally.suggest.gocharSaturn', vars: { sign: saturn }, emoji: '🪐' });

    const jupiter = leadingName(byId(facts, 'gochar.jupiter')?.value);
    if (jupiter) picks.push({ key: 'ally.suggest.gocharJupiter', vars: { sign: jupiter }, emoji: '🌟' });

    const aspect = tightestAspect(facts);
    if (aspect) picks.push({ key: 'ally.suggest.aspect', vars: { aspect }, emoji: '📐' });

    const yoga = facts.find((f) => f.id.startsWith('jyotish.yoga.'));
    if (yoga) picks.push({ key: 'ally.suggest.yoga', vars: { yoga: yoga.label }, emoji: '🕉️' });

    // Generic tail so a missing or unusable chart still offers somewhere to start.
    picks.push({ key: 'ally.suggest.career', emoji: '💼' });

    return picks.slice(0, MAX).map((p) => ({ emoji: p.emoji, text: t(p.key, p.vars) }));
  }, [facts, t]);
}

/**
 * The same starters as bare strings. Home renders them as a plain chip row and
 * has no gutter to hang a glyph in, so it takes this view rather than carrying
 * an emoji it would only have to strip.
 */
export function useAllySuggestions(): string[] {
  const chips = useAllySuggestionChips();
  return useMemo(() => chips.map((c) => c.text), [chips]);
}

function byId(facts: ChartFact[], id: string): ChartFact | undefined {
  return facts.find((f) => f.id === id);
}

/** Fact values lead with the name we want: `Venus mahadasha → …`, `Aquarius 12°…`. */
function leadingName(value: string | undefined): string | null {
  const m = value ? /^[A-Za-z]+/.exec(value.trim()) : null;
  return m ? m[0] : null;
}

/** The tightest aspect is the one worth asking about; ties keep engine order. */
function tightestAspect(facts: ChartFact[]): string | null {
  return tightestAspectFact(facts)?.text ?? null;
}

/** Same, keeping the fact itself so a citation chip can be rendered from it. */
function tightestAspectFact(facts: ChartFact[]): { text: string; fact: ChartFact } | null {
  let best: { text: string; fact: ChartFact; orb: number } | null = null;
  for (const f of facts) {
    if (!f.id.startsWith('western.aspect.')) continue;
    const m = /\(orb ([\d.]+)/.exec(f.value);
    const orb = m ? Number(m[1]) : Number.POSITIVE_INFINITY;
    if (!best || orb < best.orb) {
      best = { text: f.value.replace(/\s*\(orb[^)]*\)\s*$/, ''), fact: f, orb };
    }
  }
  return best ? { text: best.text, fact: best.fact } : null;
}

/* ===========================================================================
   Conversation trees — the deterministic half.

   Everything below is PURE and FREE. It turns a tree node plus the grounding
   fact pack into the exact words Ally opens with, the chart line underneath
   them and the first-person message that is sent if the user taps.

   Why it has to be free: a tree beat renders the moment the AI tab opens, and
   every standard AI turn spends one of Base's 10 free daily messages or a Pro
   fair-use slot. Auto-firing a generation on mount would bill someone for
   opening a tab. So Ally's line is authored copy (packages/shared/types/
   trees.ts) stitched to values the ENGINE produced — the model is only ever
   involved after a deliberate tap, and even then it just narrates facts it was
   handed.
   =========================================================================== */

export interface TreeGrounding {
  /**
   * A short chart-specific clause for this beat, e.g. "your Moon sits in
   * Vrishabha". Null when the chart cannot supply one (no positions yet, or an
   * unknown birth time made this value unresolvable).
   */
  clause: string | null;
  /** The engine facts the clause was read off — rendered as source chips. */
  facts: ChartFact[];
  /** Set when the birth time makes this beat a window rather than an answer. */
  caveat?: string;
}

const NO_GROUNDING: TreeGrounding = { clause: null, facts: [] };

/** First fact matching one of `ids`; exact matches win over suffix matches. */
function findFact(facts: ChartFact[], ids: string[]): ChartFact | undefined {
  for (const id of ids) {
    const exact = facts.find((f) => f.id === id);
    if (exact) return exact;
  }
  // Window-mode facts are re-published under `stable.` / `varying.` prefixes.
  for (const id of ids) {
    const suffixed = facts.find((f) => f.id.endsWith(`.${id}`));
    if (suffixed) return suffixed;
  }
  return undefined;
}

function findPrefixed(facts: ChartFact[], prefix: string): ChartFact | undefined {
  return facts.find((f) => f.id.startsWith(prefix));
}

/** `"Vrishabha 12°33′, Rohini pada 3"` → `"Rohini"`. */
function nakshatraOf(value: string): string | null {
  const m = /,\s*([A-Za-z][A-Za-z '-]*?)\s+pada\s+\d/.exec(value);
  return m ? m[1].trim() : null;
}

/** A whole fact value, trimmed of the parenthetical orb/precision tail. */
function plainValue(value: string): string | null {
  const text = value.replace(/\s*\(orb[^)]*\)\s*$/, '').trim();
  return text.length > 0 ? text : null;
}

type Grounder = (facts: ChartFact[]) => TreeGrounding;

/** Pulls one name out of the first matching fact and phrases it. */
function named(
  ids: string[],
  extract: (value: string) => string | null,
  phrase: (name: string) => string,
): Grounder {
  return (facts) => {
    const fact = findFact(facts, ids);
    if (!fact) return NO_GROUNDING;
    const name = extract(fact.value);
    return name ? { clause: phrase(name), facts: [fact] } : NO_GROUNDING;
  };
}

/** Same, for facts found by id prefix (yogas, transits — the id carries data). */
function namedPrefix(prefix: string, phrase: (fact: ChartFact) => string): Grounder {
  return (facts) => {
    const fact = findPrefixed(facts, prefix);
    return fact ? { clause: phrase(fact), facts: [fact] } : NO_GROUNDING;
  };
}

const MOON_SIDEREAL = ['jyotish.moon.position', 'planet.moon.siderealSign', 'planet.moon.nakshatra'];
const MOON_TROPICAL = ['western.moon.position', 'jyotish.moon.position'];

/**
 * Which engine value anchors each beat.
 *
 * A node missing from this map simply opens without a clause — the beat is
 * still shown, Ally just leads with the chart headline instead. Nothing here
 * INTERPRETS: it only names a value the engine already computed, because the
 * moment this file starts deciding what a placement means it has become the
 * astrology engine, and that lives in packages/astro-engine.
 */
const GROUNDERS: Record<string, Grounder> = {
  // --- Vedic road ----------------------------------------------------------
  'vedic.moon-sign': named(MOON_SIDEREAL, leadingName, (s) => `your Moon sits in ${s}`),
  'vedic.nakshatra': named(MOON_SIDEREAL, nakshatraOf, (n) => `your Moon lands in ${n}`),
  'vedic.nakshatra-feel': named(MOON_SIDEREAL, nakshatraOf, (n) => `you are a ${n} Moon`),
  'vedic.dasha-now': named(
    ['jyotish.dasha.current'],
    leadingName,
    (l) => `you are inside a ${l} chapter`,
  ),
  'vedic.dasha-story': named(
    ['jyotish.dasha.mahadashaSpan'],
    plainValue,
    (v) => `that chapter runs ${v}`,
  ),
  'vedic.lagna': named(['jyotish.lagna'], leadingName, (s) => `your lagna is ${s}`),
  'vedic.gochara': named(['gochar.saturn'], leadingName, (s) => `Saturn is in ${s} today`),
  'vedic.yoga': namedPrefix('jyotish.yoga.', (f) => `your chart forms ${f.label}`),
  'vedic.yoga-gate': namedPrefix('jyotish.yoga.', (f) => `there is a ${f.label} waiting in here`),

  // --- Western road --------------------------------------------------------
  'western.big-three': named(
    ['western.sun.position'],
    leadingName,
    (s) => `your Sun is in ${s} in the Western zodiac`,
  ),
  'western.rising-check': named(
    ['western.ascendant'],
    leadingName,
    (s) => `your Ascendant — your rising sign — is ${s}`,
  ),
  'western.elements': named(
    ['western.sun.position'],
    leadingName,
    (s) => `your Sun is in ${s}, which is one vote in that spread`,
  ),
  'western.aspect-tightest': (facts) => {
    const hit = tightestAspectFact(facts);
    return hit ? { clause: `your tightest angle is ${hit.text}`, facts: [hit.fact] } : NO_GROUNDING;
  },
  'western.aspect-feel': (facts) => {
    const hit = tightestAspectFact(facts);
    return hit ? { clause: `we are talking about your ${hit.text}`, facts: [hit.fact] } : NO_GROUNDING;
  },
  'western.saturn': named(
    ['western.saturn.position'],
    leadingName,
    (s) => `your Saturn is in ${s}`,
  ),
  'western.transit-now': namedPrefix('transit.', (f) => {
    const text = plainValue(f.value);
    return text ? `right now, ${text.charAt(0).toLowerCase()}${text.slice(1)}` : 'there is a transit live right now';
  }),

  // --- Self road -----------------------------------------------------------
  'self.moon-mirror': named(
    MOON_TROPICAL,
    leadingName,
    (s) => `your Moon is in ${s}`,
  ),
  'self.year-ahead': named(
    ['western.sun.position'],
    plainValue,
    (v) => `your solar return is timed off your Sun at ${v}`,
  ),

  // --- Social road ---------------------------------------------------------
  'social.milan-101': named(
    MOON_SIDEREAL,
    leadingName,
    (s) => `that method reads mostly off Moon positions, and yours is in ${s}`,
  ),
  'social.venus-mars': (facts) => {
    const venus = findFact(facts, ['western.venus.position']);
    const mars = findFact(facts, ['western.mars.position']);
    if (!venus || !mars) return NO_GROUNDING;
    const v = leadingName(venus.value);
    const m = leadingName(mars.value);
    if (!v || !m) return NO_GROUNDING;
    return { clause: `your Venus is in ${v} and your Mars in ${m}`, facts: [venus, mars] };
  },
};

/**
 * Beats that cannot be settled without an accurate birth time. Named here
 * rather than inferred, so a missing clause is never silently passed off as
 * "the chart does not say" when the real reason is "you told me roughly 6pm".
 */
const TIME_SENSITIVE = new Set([
  'vedic.lagna',
  'vedic.gochara',
  'western.big-three',
  'western.rising-check',
  'western.progressions',
  'self.year-ahead',
]);

function timeCaveat(node: TreeNode, facts: ChartFact[]): string | undefined {
  if (!TIME_SENSITIVE.has(node.id)) return undefined;
  const value = byId(facts, 'meta.timeConfidence')?.value ?? '';
  if (value.startsWith('Exact')) return undefined;
  if (value.startsWith('Approximate')) {
    return 'Your birth time is a window, so this one comes back as a range. I will say which parts of it hold either way.';
  }
  return 'Without a birth time this one can be narrowed but not settled. I will show you what still holds and what genuinely does not.';
}

/** Everything the engine can say about one beat. Pure, free, no network. */
export function groundingFor(node: TreeNode, facts: ChartFact[]): TreeGrounding {
  const base = GROUNDERS[node.id]?.(facts) ?? NO_GROUNDING;
  const caveat = timeCaveat(node, facts);
  return caveat ? { ...base, caveat } : base;
}

/**
 * One line naming what Ally can actually see, used when a beat has no anchor
 * of its own. Both zodiacs are labelled inline — a reader who has never met
 * the word "sidereal" should still know why two signs are on screen.
 */
export function chartHeadline(facts: ChartFact[]): string | null {
  const moon = leadingName(findFact(facts, MOON_SIDEREAL)?.value);
  const sun = leadingName(findFact(facts, ['western.sun.position'])?.value);
  if (moon && sun) {
    return `your Moon in ${moon} in the Indian zodiac, your Sun in ${sun} in the Western one`;
  }
  if (moon) return `your Moon in ${moon} in the Indian zodiac`;
  if (sun) return `your Sun in ${sun} in the Western zodiac`;
  return null;
}

/** Ally's authored line for a beat, with the chart clause led in front of it. */
export function allyBeat(node: TreeNode, grounding: TreeGrounding): string {
  if (!grounding.clause) return node.prompt;
  const lead = grounding.clause.charAt(0).toUpperCase() + grounding.clause.slice(1);
  return `${lead}. ${node.prompt}`;
}

/** The chip label for a beat. Nodes without one fall back to a short prompt. */
export function beatLabel(node: TreeNode): string {
  if (node.label) return node.label;
  const words = node.prompt.split(/\s+/).slice(0, 5).join(' ');
  return `${words}…`;
}

export type StepMode =
  /** Sends a first-person question to Ally. Spends one message. */
  | 'ask'
  /** Ally asks; the user types the answer in the composer. Their reply is the message. */
  | 'answer'
  /** A gate. Opening it is bookkeeping — no model call, no cost. */
  | 'open';

export interface StepAction {
  mode: StepMode;
  label: string;
  /** True when finishing this beat spends one of the day's AI messages. */
  costsMessage: boolean;
}

export function stepAction(node: TreeNode): StepAction {
  switch (node.kind) {
    case 'reveal':
      return { mode: 'ask', label: 'Read it from my chart', costsMessage: true };
    case 'reflection':
      return { mode: 'ask', label: 'Reflect it back to me', costsMessage: true };
    case 'question':
      return { mode: 'answer', label: 'Answer this', costsMessage: true };
    case 'unlock':
      return { mode: 'open', label: 'Open this branch', costsMessage: false };
  }
}

/**
 * The message actually sent when a beat is tapped, written in the user's own
 * voice because it is rendered as their chat bubble. The authored prompt is
 * quoted rather than spoken, so a line written for Ally ("Here is yours…")
 * never ends up coming out of the user's mouth.
 */
export function askTextFor(
  tree: ConversationTree,
  node: TreeNode,
  grounding: TreeGrounding,
): string {
  const seen = grounding.clause ? ` I can see ${grounding.clause}.` : '';
  const path = `I am walking the “${tree.title}” path with you.`;
  switch (node.kind) {
    case 'reveal':
      return (
        `${path} Show me this part of my chart: “${beatLabel(node)}”.${seen} ` +
        'Use only my calculated values, explain every term you use in plain English, ' +
        'and tell me what it MEANS rather than what is going to happen.'
      );
    case 'reflection':
      return (
        `${path} Reflect this back to me: “${beatLabel(node)}”.${seen} ` +
        'Use what I have already told you in this conversation alongside my calculated chart, ' +
        'and be straight about where the two do not line up.'
      );
    case 'question':
    case 'unlock':
      // Neither is sent by the UI (a question is answered in the composer, a
      // gate is free bookkeeping), but the function stays total so a future
      // caller cannot get `undefined` back and send it anyway.
      return `${path} ${beatLabel(node)}.${seen}`;
  }
}

/* --- where the user is, across all four paths ----------------------------- */

export interface TreeStep {
  tree: ConversationTree;
  node: TreeNode;
}

/**
 * The beat a tree is sitting on: the stored current node when it is still
 * open, otherwise the shallowest open one. Null means the tree is finished.
 */
export function currentStep(tree: ConversationTree, progress?: TreeProgress): TreeNode | null {
  if (!progress) return rootNodes(tree)[0] ?? null;
  const open = availableNodes(tree, progress);
  if (open.length === 0) return null;
  const stored = open.find((n) => n.id === progress.currentNodeId);
  if (stored) return stored;
  let shallowest = open[0];
  for (const node of open) {
    if (nodeDepth(tree, node.id) < nodeDepth(tree, shallowest.id)) shallowest = node;
  }
  return shallowest;
}

/**
 * Which beat Ally opens the tab with: the path the user is furthest into, or
 * the first unstarted one. Deterministic — ties keep CONVERSATION_TREES order,
 * so the same state always produces the same opener and the screen does not
 * reshuffle itself between renders.
 *
 * The social path is excluded outright while connections are off. It is not
 * "hidden but selectable"; with the opt-in off it is not a path that exists.
 */
export function openingStep(
  progress: Record<string, TreeProgress>,
  socialEnabled: boolean,
): TreeStep | null {
  let best: { step: TreeStep; done: number } | null = null;
  for (const tree of CONVERSATION_TREES) {
    if (tree.system === 'social' && !socialEnabled) continue;
    const node = currentStep(tree, progress[tree.id]);
    if (!node) continue;
    const done = progress[tree.id]?.completedNodeIds.length ?? 0;
    if (!best || done > best.done) best = { step: { tree, node }, done };
  }
  return best?.step ?? null;
}
